/**
 * App — the editor shell: selection, tool routing, feature operations.
 * Panels subscribe via onUI and re-render from (doc, app) state; all
 * modeling goes through doc.apply so undo labels stay meaningful.
 */

import * as THREE from 'three/webgpu';

import { CadDocument } from '@core/document';
import { MATERIALS, materialById } from '@core/materials';
import { BrepKernel } from '@core/geometry/brepKernel';
import type { GeomKernel, MeshData } from '@core/geometry/types';
import { hashBytes } from '@core/io/mesh';
import { saveBrepText, saveParsedMesh } from '@core/storage/assets';
import { db, type ProjectRecord } from '@core/storage/db';
import { buildProjectFile, restoreProjectFile } from '@core/storage/projectFile';
import { setSetting } from '@core/storage/settings';
import type { EvaluationReport } from '@core/timeline/evaluator';
import { evalParamTable, evaluateExpr, type Expr } from '@core/expr';
import { Timeline } from '@core/timeline/timeline';
import type { ExtrudeParams, FeatureId, SketchConstraint } from '@core/timeline/types';
import { ref } from '@core/timeline/types';
import { exportMeshes, parseMeshFile, type ExportFormat } from '@workers/ioClient';
import type { StandardView, Viewport } from '@render/viewport';
import type { BodyAppearance } from '@render/bodyMaterials';
import { Assistant } from './ai/assistant';
import { CadApi } from './ai/cadApi';
import { AssistantToolbox } from './ai/tools';
import { MeasureTool } from './tools/measureTool';
import { MoveGizmo, type PostGesture } from './tools/moveGizmo';
import { PushPullTool } from './tools/pushPull';
import { MarqueeTool } from './tools/marqueeTool';
import { ScaleGizmo } from './tools/scaleGizmo';
import { SketchTool, type EntityKind } from './tools/sketchTool';
import { showContextMenu, type ContextItem } from '@ui/contextMenu';
import type { DimInput } from '@ui/dimInput';

export class App {
  /** Multi-selection (shift-click). Most tools act on a SINGLE selection. */
  selection: FeatureId[] = [];

  /** The single selected feature, or null when 0 or 2+ are selected. */
  get selected(): FeatureId | null {
    return this.selection.length === 1 ? this.selection[0]! : null;
  }
  /** Which slide-over panel is open. */
  activePanel: 'ai' | 'assets' | 'bodies' | 'projects' | 'assistant' | 'params' | null = null;
  /** Section view config (null = off) + whether its control panel shows. */
  section: { axis: 'X' | 'Y' | 'Z'; offset: number; flip: boolean } | null = null;
  sectionOpen = false;
  readonly measure: MeasureTool;
  /** LLM copilot: shared programmatic API + tool registry + chat agent. */
  readonly cadApi: CadApi;
  readonly toolbox: AssistantToolbox;
  readonly assistant: Assistant;
  /** Two-step sweep flow: profile sketch armed, waiting for a path sketch. */
  pendingSweepProfile: FeatureId | null = null;
  /** Push/Pull tool armed: drag an extrude cap to change its distance. When
   * OFF (default), the Select tool never edits geometry — drags orbit. */
  pushPullMode = false;
  /** Orbit tool armed: left-drag rotates the view (instead of the marquee).
   * Mutually exclusive with pushPullMode. */
  orbitMode = false;
  /** Transient status line for import/export feedback. */
  ioStatus = '';
  /** '?' quick-reference overlay. */
  cheatsheetOpen = false;
  /** Drawing sheet overlay + its last generated model. */
  drawingOpen = false;
  sheet: import('@core/drawing/layout').SheetModel | null = null;
  sheetBusy = false;
  sheetOptions: { size: 'A3' | 'A4'; scale: number | 'auto' } = { size: 'A3', scale: 'auto' };
  /** User-placed dimensions (sheet coords + value text); re-applied on regen. */
  customDims: { a: [number, number]; b: [number, number]; text: string }[] = [];

  /** Generate the drawing sheet from the current model (B-rep bodies). */
  async generateDrawing(): Promise<void> {
    const report = this.doc.lastReport;
    const ids: string[] = [];
    if (report) {
      for (const [id, r] of report.results) {
        if (r.kind === 'body' && !report.consumed.has(id) && r.brepId && r.mesh.positions.length > 0) {
          ids.push(r.brepId);
        }
      }
    }
    if (ids.length === 0) {
      this.setIoStatus('Nothing to draw — the model has no B-rep bodies');
      return;
    }
    const kernel = this.kernel as unknown as {
      projectView?: (
        ids: readonly string[],
        dir: [number, number, number],
        xdir: [number, number, number],
        section?: { axis: 'X' | 'Y' | 'Z'; offset: number; flip: boolean },
      ) => Promise<import('@core/drawing/layout').ViewGeometry>;
    };
    if (!kernel.projectView) {
      this.setIoStatus('Drawings need the B-rep kernel');
      return;
    }
    this.sheetBusy = true;
    this.notifyUI();
    try {
      const S3 = 0.5773502691896258;
      const [front, top, right, iso] = await Promise.all([
        kernel.projectView(ids, [0, -1, 0], [1, 0, 0]),
        kernel.projectView(ids, [0, 0, 1], [1, 0, 0]),
        kernel.projectView(ids, [1, 0, 0], [0, 1, 0]),
        kernel.projectView(ids, [S3, -S3, S3], [0.7071, 0.7071, 0]),
      ]);
      // Section view: reuse the live section tool's plane when it's on.
      let section: { geo: import('@core/drawing/layout').ViewGeometry; label: string } | undefined;
      if (this.section) {
        const s = this.section;
        const dir: [number, number, number] =
          s.axis === 'X' ? [1, 0, 0] : s.axis === 'Y' ? [0, 1, 0] : [0, 0, 1];
        const xdir: [number, number, number] = s.axis === 'X' ? [0, 1, 0] : [1, 0, 0];
        const geo = await kernel.projectView(ids, dir, xdir, s);
        section = { geo, label: `SECTION ${s.axis}=${Math.round(s.offset * 10) / 10}` };
      }
      const { layoutSheet, appendAlignedDim } = await import('@core/drawing/layout');
      this.sheet = layoutSheet(
        {
          front, top, right, iso, section,
          projectName: this.doc.name,
          dateText: new Date().toISOString().slice(0, 10),
        },
        {
          size: this.sheetOptions.size,
          ...(this.sheetOptions.scale !== 'auto' ? { scale: this.sheetOptions.scale } : {}),
        },
      );
      for (const d of this.customDims) appendAlignedDim(this.sheet, d.a, d.b, d.text);
    } catch (err) {
      this.setIoStatus(`Drawing failed: ${err instanceof Error ? err.message : err}`);
    } finally {
      this.sheetBusy = false;
      this.notifyUI();
    }
  }

  toggleDrawing(): void {
    this.drawingOpen = !this.drawingOpen;
    if (this.drawingOpen && !this.sheetBusy) void this.generateDrawing();
    this.notifyUI();
  }
  /** One-shot: properties panel focuses this field's input on next render. */
  focusPropertyField: string | null = null;
  readonly sketch: SketchTool;
  private readonly pushPull: PushPullTool;
  private readonly marquee: MarqueeTool;
  private readonly scaleGizmo: ScaleGizmo;
  private readonly moveGizmo: MoveGizmo;
  private readonly dim: DimInput;
  /** After a move/rotate drag: typing "5x" arrays copies, a number = exact. */
  private postGesture: PostGesture | null = null;
  private uiListeners = new Set<() => void>();
  private _doc: CadDocument;
  private docUnsub: (() => void) | null = null;

  /** The open document. Panels/tools always read through this getter. */
  get doc(): CadDocument {
    return this._doc;
  }

  constructor(
    initialDoc: CadDocument,
    private readonly kernel: GeomKernel,
    readonly viewport: Viewport,
    dim: DimInput,
  ) {
    this._doc = initialDoc;
    this.dim = dim;
    this.sketch = new SketchTool(viewport, this, dim);
    this.pushPull = new PushPullTool(viewport, this, dim);
    this.marquee = new MarqueeTool(viewport, this, (ids, additive) => this.selectByBox(ids, additive));
    this.scaleGizmo = new ScaleGizmo(viewport, this, dim);
    this.moveGizmo = new MoveGizmo(viewport, this, dim);
    this.measure = new MeasureTool(viewport, this);
    this.measure.onStateChange = () => this.notifyUI();
    this.sketch.onStateChange = () => this.notifyUI();

    viewport.controls.onDragStart = (e) =>
      this.sketch.active
        ? this.sketch.tryStart(e)
        : (this.scaleGizmo.tryStart(e) ??
          this.moveGizmo.tryStart(e) ??
          // Push/pull is an explicit tool now — the Select tool never edits
          // geometry, so a drag meant to orbit/select can't accidentally
          // extrude a cap. Gizmo handles above stay live regardless.
          (this.pushPullMode
            ? this.pushPull.tryStart(e)
            : this.orbitMode
              ? null // Orbit tool: leave the drag to the camera (orbits)
              : // Select tool: left-drag paints a marquee (SketchUp-style);
                // alt+left / middle fall through (null) so the camera orbits.
                this.marquee.tryStart(e)));
    viewport.controls.onTap = (x, y, additive) => this.handleTap(x, y, additive);
    viewport.controls.onContextTap = (x, y) => this.showContextMenu(x, y);
    const focusDbl = viewport.controls.onDoublePress;
    viewport.controls.onDoublePress = (x, y) => {
      if (this.sketch.handleDoubleClick()) return; // ends a line chain
      focusDbl?.(x, y);
    };
    this.docUnsub = initialDoc.subscribe(this.onDocChange);
    window.addEventListener('keydown', this.onKeyDown);

    this.cadApi = new CadApi(
      () => this.doc,
      {
        select: (id) => this.select(id),
        fitView: () => viewport.fitAll(),
        capture: (view) => viewport.captureFrom(view as StandardView | undefined, 768, 'jpeg'),
        createProject: async (name) => {
          await this.newProject(name ?? 'Untitled');
          return this.doc.id;
        },
        switchProject: (id) => this.switchProject(id),
        deleteProject: (id) => this.deleteProjectNoConfirm(id),
        showStress: (id, vm, info) => this.showStressResult(id, vm, info),
        clearStress: () => this.clearStress(),
      },
    );
    this.toolbox = new AssistantToolbox(this.cadApi);
    this.toolbox.onChange = () => this.notifyUI();
    void this.toolbox.load();
    this.assistant = new Assistant(this.cadApi, this.toolbox);
  }

  /** Effective material per visible body (own or inherited via consumption). */
  private bodyAppearances(report: EvaluationReport): Map<FeatureId, BodyAppearance> {
    const map = new Map<FeatureId, BodyAppearance>();
    for (const [id, r] of report.results) {
      if (r.kind !== 'body' || report.consumed.has(id)) continue;
      const resolved = this.doc.resolveMaterial(id);
      if (resolved) map.set(id, resolved);
    }
    return map;
  }

  /** Re-render current geometry (display-mode flips, material edits). */
  private refreshBodies(): void {
    const report = this.doc.lastReport;
    if (report) this.viewport.updateBodies(report, this.bodyAppearances(report));
  }

  private onDocChange = (_d: CadDocument, report: EvaluationReport | null): void => {
    if (report) this.viewport.updateBodies(report, this.bodyAppearances(report));
    this.scaleGizmo.sync(this.selected); // handles track the body as it changes
    this.moveGizmo.sync(this.selected);
    if (this.sketch.active) this.sketch.refreshModelSnaps(); // edges may have moved
    if (this.measure.active) this.measure.refreshCandidates();
    // Pick modes whose target feature vanished (delete/undo) must die with
    // it — otherwise Enter would commit against a ghost id.
    const gone = (id: FeatureId | undefined): boolean =>
      id !== undefined && !this.doc.timeline.get(id);
    if (
      gone(this.edgePick?.targetId) ||
      gone(this.holePick?.targetId) ||
      gone(this.feaPick?.targetId) ||
      gone(this.matePick?.moving) ||
      gone(this.matePick?.fixed)
    ) {
      this.edgePick = null;
      this.holePick = null;
      this.feaPick = null;
      this.feaDialog?.remove();
      this.feaDialog = null;
      this.matePick = null;
      this.viewport.overlay.clear();
      this.viewport.requestRender();
    }
    // A stress result is a snapshot of one geometry state — edits stale it.
    if (this.feaLegend && !this.feaBusy) this.clearStress();
    this.notifyUI();
  };

  /** MoveGizmo host hook: opens the "type 5x / exact value" window. */
  armPostGesture(pg: PostGesture | null): void {
    this.postGesture = pg;
  }

  /** Assign/clear a body's material (undoable; render-only, no regen). */
  async setMaterial(id: FeatureId, material: string | undefined, color?: string): Promise<void> {
    const name = material ? (materialById(material)?.name ?? material) : 'none';
    await this.doc.apply(`Material: ${name}`, (t) => t.setMaterial(id, material, color));
    this.setIoStatus(material ? `${this.doc.timeline.get(id)?.name}: ${name}` : 'Material cleared');
  }

  /** Second-level context menu: the material swatch picker for one body. */
  private showMaterialMenu(x: number, y: number, id: FeatureId): void {
    const node = this.doc.timeline.get(id);
    const items: ContextItem[] = [{ label: 'Material', header: true }];
    for (const m of MATERIALS) {
      items.push({
        label: m.name,
        swatch: m.appearance.color,
        hint: `${m.physical.density} g/cm³`,
        action: () => void this.setMaterial(id, m.id),
      });
    }
    items.push({
      label: 'Custom color…',
      separator: true,
      action: () => {
        const current = this.doc.resolveMaterial(id);
        const hex = window.prompt(
          'Color override (#rrggbb) — applied on top of the material',
          current?.color ?? current?.material?.appearance.color ?? '#b7bfcc',
        );
        if (hex && /^#[0-9a-f]{6}$/i.test(hex.trim())) {
          void this.setMaterial(id, node?.material, hex.trim());
        }
      },
    });
    if (node?.material !== undefined || node?.materialColor !== undefined) {
      items.push({ label: 'Clear material', action: () => void this.setMaterial(id, undefined) });
    }
    showContextMenu(x, y, items);
  }

  // --- context menu (smart right-click) --------------------------------------------

  /** Build and show the context menu for a right-click at (x, y). */
  showContextMenu(x: number, y: number): void {
    // Menu actions raycast/consume 3D points — never against the deformed view.
    this.clearStress();
    showContextMenu(x, y, this.buildContextItems(x, y));
  }

  private buildContextItems(x: number, y: number): ContextItem[] {
    const items: ContextItem[] = [];

    // --- sketch mode: drawing-centric menu ---
    if (this.sketch.active) {
      items.push({ label: 'Sketching', header: true });
      for (const [kind, label, key] of [
        ['line', 'Line', 'L'],
        ['rect', 'Rectangle', 'R'],
        ['circle', 'Circle', 'C'],
        ['arc2', '2-Point Arc', 'A'],
        ['arc', 'Center Arc', ''],
      ] as const) {
        items.push({
          label,
          hint: this.sketch.entityKind === kind ? '●' : key,
          action: () => this.sketch.setEntityKind(kind),
        });
      }
      // Constraints: right-click near an entity offers constraint actions;
      // pair constraints go through "mark", then right-click the second one.
      const hitEnt = this.sketch.entityAt(x, y);
      const marked = this.sketch.markedEntity;
      if (hitEnt !== null) {
        const ent = this.sketch.currentEntities[hitEnt]!;
        items.push({ label: `Constrain ${ent.kind} #${hitEnt}`, header: true, separator: true });
        if (ent.kind === 'line') {
          items.push({
            label: 'Horizontal',
            action: () => this.applyConstraint({ kind: 'horizontal', entity: hitEnt }),
          });
          items.push({
            label: 'Vertical',
            action: () => this.applyConstraint({ kind: 'vertical', entity: hitEnt }),
          });
          items.push({
            label: 'Length…',
            action: () =>
              this.promptConstraintValue('length', x, y, (v) =>
                this.applyConstraint({
                  kind: 'distance',
                  a: { entity: hitEnt, point: 'a' },
                  b: { entity: hitEnt, point: 'b' },
                  value: v,
                }),
              ),
          });
        }
        if (ent.kind === 'circle' || ent.kind === 'arc') {
          items.push({
            label: 'Radius…',
            action: () =>
              this.promptConstraintValue('radius', x, y, (v) =>
                this.applyConstraint({ kind: 'radius', entity: hitEnt, value: v }),
              ),
          });
        }
        const pr = this.sketch.pointRefAt(hitEnt, x, y);
        if (pr) {
          items.push({
            label: `Fix ${pr.point} point`,
            action: () => {
              const at = this.sketch.pointPosition(pr);
              if (at) this.applyConstraint({ kind: 'fixed', point: pr, at });
            },
          });
        }
        if (marked !== null && marked !== hitEnt) {
          const other = this.sketch.currentEntities[marked]!;
          if (ent.kind === 'line' && other.kind === 'line') {
            items.push({
              label: `Parallel to #${marked}`,
              action: () => this.applyConstraint({ kind: 'parallel', a: marked, b: hitEnt }),
            });
            items.push({
              label: `Perpendicular to #${marked}`,
              action: () => this.applyConstraint({ kind: 'perpendicular', a: marked, b: hitEnt }),
            });
          }
          items.push({
            label: `Equal to #${marked}`,
            action: () => this.applyConstraint({ kind: 'equal', a: marked, b: hitEnt }),
          });
          const prA = this.sketch.pointRefAt(marked, x, y);
          if (prA && pr) {
            items.push({
              label: `Distance to #${marked}…`,
              action: () =>
                this.promptConstraintValue('distance', x, y, (v) =>
                  this.applyConstraint({ kind: 'distance', a: prA, b: pr, value: v }),
                ),
            });
            items.push({
              label: `Coincident with #${marked}.${prA.point}`,
              action: () => this.applyConstraint({ kind: 'coincident', a: prA, b: pr }),
            });
          }
        }
        items.push({
          label: marked === hitEnt ? 'Unmark' : 'Mark for pair constraint',
          action: () => {
            this.sketch.markedEntity = marked === hitEnt ? null : hitEnt;
            this.notifyUI();
          },
        });
        if (ent.kind === 'line') {
          items.push({
            label: 'Fillet corner…',
            action: () => {
              const uv = this.sketch.uvAt(x, y);
              this.promptConstraintValue('fillet radius', x, y, (v) => {
                const r = typeof v === 'number' ? v : NaN;
                if (!Number.isFinite(r) || r <= 0) {
                  this.setIoStatus('Fillet radius must be a positive number');
                  return;
                }
                if (!uv || !this.sketch.filletCornerAt(hitEnt, uv, r)) {
                  this.setIoStatus('No filletable corner — needs two lines sharing an endpoint, radius small enough');
                }
              });
            },
          });
        }
        if (marked !== null && marked !== hitEnt) {
          const other = this.sketch.currentEntities[marked]!;
          const isCirc = (k: string): boolean => k === 'circle' || k === 'arc';
          if ((ent.kind === 'line' && isCirc(other.kind)) || (isCirc(ent.kind) && other.kind === 'line')) {
            items.push({
              label: `Tangent to #${marked}`,
              action: () => this.applyConstraint({ kind: 'tangent', a: marked, b: hitEnt }),
            });
          }
          if ((isCirc(ent.kind) || ent.kind === 'ellipse') && (isCirc(other.kind) || other.kind === 'ellipse')) {
            items.push({
              label: `Concentric with #${marked}`,
              action: () => this.applyConstraint({ kind: 'concentric', a: marked, b: hitEnt }),
            });
          }
        }
        if (['line', 'circle', 'arc', 'rect', 'ellipse'].includes(ent.kind)) {
          items.push({
            label: 'Offset…',
            action: () => {
              const side = this.sketch.uvAt(x, y) ?? undefined;
              this.promptConstraintValue('offset distance', x, y, (v) => {
                const d = typeof v === 'number' ? v : NaN;
                if (!Number.isFinite(d) || d === 0) {
                  this.setIoStatus('Offset needs a nonzero number (± = shrink/grow)');
                  return;
                }
                if (!this.sketch.offsetEntityAt(hitEnt, d, side)) {
                  this.setIoStatus('Offset would make this entity degenerate');
                }
              });
            },
          });
        }
        items.push({
          label: 'Delete entity',
          hint: '⌫',
          action: () => this.sketch.deleteEntity(hitEnt),
        });
        if (this.sketch.constraintCount > 0) {
          items.push({
            label: `${this.sketch.constraintCount} constraint(s) — manage in Properties after finishing`,
            disabled: true,
            action: () => undefined,
          });
        }
      }

      items.push({ label: 'Finish sketch', separator: true, hint: '⏎', action: () => void this.finishSketch() });
      items.push({ label: 'Cancel sketch', action: () => this.cancelSketch() });
      return items;
    }

    const hit = this.viewport.raycastBodies(x, y);

    // --- on a body: feature-aware actions ---
    if (hit) {
      const feature = this.doc.timeline.get(hit.featureId);
      if (!feature) return items;
      const id = feature.id;
      const inSelection = this.selection.includes(id);
      const bodySel = this.selection.filter((s) => {
        const r = this.doc.lastReport?.results.get(s);
        return r?.kind === 'body' && !this.doc.lastReport?.consumed.has(s);
      });

      items.push({ label: feature.name, header: true });
      if (!inSelection) {
        items.push({ label: 'Select', action: () => this.select(id) });
      }
      if (inSelection && bodySel.length >= 2) {
        items.push({
          label: `Group ${bodySel.length} bodies`,
          action: () => void this.groupSelected(),
        });
      }
      if (this.canMate) {
        const [a, b] = this.selection;
        items.push({
          label: `Mate ${this.doc.timeline.get(a!)?.name} onto ${this.doc.timeline.get(b!)?.name}`,
          action: () => void this.mateSelected(),
        });
        items.push({
          label: 'Concentric mate… (pick two cylindrical faces)',
          action: () => this.startConcentricMate(),
        });
      }
      if (this.selection.length === 2 && bodySel.length === 2) {
        items.push({
          label: 'Check interference (overlap volume)',
          action: () => void this.checkInterference(),
        });
      }

      items.push({
        label: 'Sketch on this face',
        separator: true,
        action: () => this.sketch.startOnFace(x, y),
      });
      const actOn = (fn: () => void) => () => {
        this.select(id);
        fn();
      };
      items.push({ label: 'Move / Rotate', action: actOn(() => void this.moveSelected()) });
      items.push({ label: 'Scale', action: actOn(() => void this.scaleSelected()) });
      items.push({ label: 'Mirror', action: actOn(() => void this.mirrorSelected()) });
      {
        const current = this.doc.resolveMaterial(id);
        items.push({
          label: `Material…${current?.material ? ` (${current.material.name})` : ''}`,
          swatch: current?.color ?? current?.material?.appearance.color,
          action: () => this.showMaterialMenu(x, y, id),
        });
      }
      // Dress-ups need an exact B-rep body (present on the OCCT kernel).
      const hitResult = this.doc.lastReport?.results.get(id);
      if (hitResult?.kind === 'body' && hitResult.brepId) {
        items.push({
          label: 'Fillet edges… (pick, then Enter)',
          separator: true,
          action: () => this.startEdgePick('fillet', id),
        });
        items.push({
          label: 'Chamfer edges… (pick, then Enter)',
          action: () => this.startEdgePick('chamfer', id),
        });
        items.push({
          label: 'Holes… (click positions, then Enter)',
          action: () => this.startHolePick(id),
        });
        items.push({
          label: 'Pattern…',
          action: actOn(() => void this.patternSelected()),
        });
        items.push({ label: 'Shell (hollow)…', action: () => void this.addDressup('shell', id) });
        items.push({
          label: 'Draft walls… (mold angle)',
          action: actOn(() => void this.addDraft(id)),
        });
        items.push({
          label: 'Mass properties (volume · area · CoM)',
          action: actOn(() => void this.massPropsSelected()),
        });
        items.push({
          label: 'Stress analysis… (fix faces, load, solve)',
          action: () => this.startFeaPick(id),
        });
      }
      items.push({
        label: 'Make component instance',
        action: actOn(() => void this.instanceSelected()),
      });

      if (feature.type === 'extrude') {
        const sketchId = (feature.params as ExtrudeParams).profile.$feature;
        const sk = this.doc.timeline.get(sketchId);
        if (sk) {
          items.push({
            label: `Edit ${sk.name}`,
            separator: true,
            action: () => this.editSketch(sketchId),
          });
        }
        items.push({
          label: 'Edit distance…',
          action: actOn(() => {
            this.focusPropertyField = 'Distance';
            this.notifyUI();
          }),
        });
      }
      if (feature.type === 'group') {
        items.push({
          label: 'Ungroup (delete group)',
          separator: true,
          action: () => void this.deleteFeature(id),
        });
      }

      items.push({
        label: 'Focus camera here',
        separator: true,
        action: () => this.viewport.controls.setTarget(hit.point),
      });
      items.push({
        label: feature.suppressed ? 'Unsuppress' : 'Suppress',
        action: () => void this.setSuppressed(id, !feature.suppressed),
      });
      items.push({
        label: 'Delete',
        hint: '⌫',
        action: () => void this.deleteFeature(id),
      });
      return items;
    }

    // --- empty space: workspace actions ---
    items.push({ label: 'Sketch here', action: () => this.sketch.startOnFace(x, y) });
    items.push({ label: 'Fit view', hint: 'F', action: () => this.viewport.fitAll() });
    if (this.selection.length > 0) {
      items.push({ label: 'Deselect', hint: 'esc', action: () => this.select(null) });
    }
    items.push({
      label: 'Undo',
      hint: '⌘Z',
      separator: true,
      disabled: !this.doc.canUndo,
      action: () => void this.doc.undo(),
    });
    items.push({
      label: 'Redo',
      hint: '⌘⇧Z',
      disabled: !this.doc.canRedo,
      action: () => void this.doc.redo(),
    });
    items.push({
      label: 'Ask the assistant…',
      separator: true,
      action: () => {
        this.activePanel = 'assistant';
        this.notifyUI();
      },
    });
    return items;
  }

  // --- parameters / constraints / measure / section --------------------------------

  /** Evaluated document parameter values (expression scope). */
  get paramScope(): Record<string, number> {
    return evalParamTable(this.doc.timeline.params).values;
  }

  async setParameter(name: string, expr: string): Promise<void> {
    await this.doc.apply(`Set parameter ${name} = ${expr}`, (t) => t.setParam(name, expr));
  }

  async deleteParameter(name: string): Promise<void> {
    await this.doc.apply(`Delete parameter ${name}`, (t) => t.deleteParam(name));
  }

  private applyConstraint(c: SketchConstraint): void {
    const err = this.sketch.addConstraint(c, this.paramScope);
    this.setIoStatus(err ? `Constraint failed: ${err}` : 'Constraint added — sketch re-solved');
  }

  /**
   * Dimension-value entry for constraints: a number commits as-is; anything
   * else is validated as an expression against the parameter table and
   * stored as {$expr} (stays live when the parameter changes).
   */
  private promptConstraintValue(
    label: string,
    clientX: number,
    clientY: number,
    apply: (v: number | Expr) => void,
  ): void {
    this.dim.show(label, clientX, clientY, (_values, raw) => {
      const text = raw.trim();
      const num = Number(text);
      if (Number.isFinite(num)) {
        apply(num);
        return;
      }
      try {
        evaluateExpr(text, this.paramScope);
      } catch (err) {
        this.setIoStatus(
          `Expression error: ${err instanceof Error ? err.message : String(err)}`,
        );
        return;
      }
      apply({ $expr: text });
    });
    this.dim.acceptText = true;
    this.dim.beginEdit('');
  }

  toggleMeasure(): void {
    this.clearStress();
    if (this.sketch.active) void this.finishSketch();
    // Measure and the Select sub-modes are mutually exclusive — at most one
    // tool drives the pointer at a time.
    if (!this.measure.active) { this.pushPullMode = false; this.orbitMode = false; }
    this.measure.toggle();
  }

  /** Toggle the section-view control panel; opening turns the section on. */
  toggleSectionPanel(): void {
    this.sectionOpen = !this.sectionOpen;
    if (!this.sectionOpen) {
      this.applySection(null);
    } else {
      const cfg = this.section ?? (() => {
        const r = this.viewport.bodiesRange('Z');
        return { axis: 'Z' as const, offset: (r.min + r.max) / 2, flip: false };
      })();
      this.applySection(cfg);
    }
  }

  applySection(cfg: { axis: 'X' | 'Y' | 'Z'; offset: number; flip: boolean } | null): void {
    this.clearStress(); // display-epoch rebuild drops the colors — drop the legend too
    this.section = cfg;
    const structural = this.viewport.setSection(cfg);
    // On/off swaps between plain and cutaway materials — rebuild bodies.
    // Plane moves while on are uniform updates; no rebuild needed.
    if (structural) this.refreshBodies();
    this.notifyUI();
  }

  // --- project management --------------------------------------------------------

  /** Cancel every transient interaction mode (project switch, safety). */
  private resetModes(): void {
    this.sketch.cancel();
    this.pendingSweepProfile = null;
    this.pushPullMode = false;
    this.orbitMode = false;
    this.edgePick = null;
    this.holePick = null;
    this.matePick = null;
    this.feaPick = null;
    this.feaDialog?.remove();
    this.feaDialog = null;
    this.clearStress();
    if (this.measure.active) this.measure.stop();
    this.viewport.overlay.clear();
  }

  /** Toggle the Push/Pull tool (drag a cap face to change extrude distance). */
  togglePushPull(): void {
    this.clearStress();
    this.pushPullMode = !this.pushPullMode;
    if (this.pushPullMode) {
      this.orbitMode = false; // the two Select sub-modes are exclusive
      if (this.measure.active) this.measure.stop();
      if (this.sketch.active) void this.finishSketch();
    }
    this.notifyUI();
  }

  /** Toggle the Orbit tool (left-drag rotates the view instead of marqueeing). */
  toggleOrbit(): void {
    this.orbitMode = !this.orbitMode;
    if (this.orbitMode) {
      this.pushPullMode = false;
      if (this.measure.active) this.measure.stop();
      if (this.sketch.active) void this.finishSketch();
    }
    this.notifyUI();
  }

  /** Swap in another document: flush the old one, rewire, re-evaluate. */
  private async setDocument(doc: CadDocument): Promise<void> {
    this.resetModes();
    // Per-project view state: the old project's drawing sheet, custom dims,
    // and section plane are meaningless against the new model.
    this.sheet = null;
    this.customDims = [];
    this.drawingOpen = false;
    this.sectionOpen = false;
    this.applySection(null);
    await this.captureThumbnail().catch(() => undefined);
    await this._doc.flushSave();
    this.docUnsub?.();
    this._doc = doc;
    this.docUnsub = doc.subscribe(this.onDocChange);
    this.selection = [];
    this.viewport.setSelected(null);
    await doc.refresh();
    this.viewport.fitAll();
    await setSetting('lastProject', doc.id);
    this.notifyUI();
  }

  async listProjects(): Promise<ProjectRecord[]> {
    return db.projects.orderBy('updatedAt').reverse().toArray();
  }

  async switchProject(id: string): Promise<void> {
    if (id === this._doc.id) return;
    const doc = await CadDocument.open(id, this.kernel);
    if (doc) await this.setDocument(doc);
  }

  async newProject(name = 'Untitled'): Promise<void> {
    const doc = new CadDocument(crypto.randomUUID(), name, this.kernel);
    await doc.save();
    await this.setDocument(doc);
  }

  async duplicateProject(): Promise<void> {
    await this._doc.flushSave();
    const rec = await db.projects.get(this._doc.id);
    if (!rec) return;
    const id = crypto.randomUUID();
    const now = Date.now();
    await db.projects.put({ ...rec, id, name: `${rec.name} copy`, createdAt: now, updatedAt: now });
    await this.switchProject(id);
  }

  async deleteProject(id: string): Promise<void> {
    const rec = await db.projects.get(id);
    if (!rec) return;
    if (!confirm(`Delete project "${rec.name}"? This cannot be undone.`)) return;
    await this.deleteProjectNoConfirm(id);
  }

  /**
   * Delete a project WITHOUT the GUI confirm dialog — the programmatic path
   * for CadApi/MCP (the caller already decided). If it's the live project,
   * switch to the most-recent survivor, or spawn a blank one if it was last.
   */
  async deleteProjectNoConfirm(id: string): Promise<void> {
    // Switch away BEFORE deleting when it's the live project: setDocument
    // flushes the OUTGOING doc's pending save (+thumbnail), which would
    // re-persist `id` if we deleted it first. Only after we're off `id` does
    // db.delete stick.
    if (id === this._doc.id) {
      const survivor = (await this.listProjects()).find((p) => p.id !== id);
      if (survivor) await this.switchProject(survivor.id);
      else await this.newProject(); // was the last project → open a fresh one
    }
    await db.projects.delete(id);
    this.notifyUI();
  }

  async renameProject(name: string): Promise<void> {
    await this._doc.rename(name);
    this.notifyUI();
  }

  /** Small viewport snapshot stored on the project record (browser cards). */
  async captureThumbnail(): Promise<void> {
    // Time-boxed: capture awaits a rendered frame, and rAF never fires in a
    // HIDDEN tab — without the race, switching projects from a backgrounded
    // tab (or automation) would hang forever on a cosmetic thumbnail.
    const thumbnail = await Promise.race([
      this.viewport.capture(240),
      new Promise<null>((r) => setTimeout(() => r(null), 1500)),
    ]);
    if (thumbnail) await db.projects.update(this._doc.id, { thumbnail });
  }

  /** Download the open project as a self-contained .novacad file. */
  async downloadProject(): Promise<void> {
    await this._doc.flushSave();
    const blob = await buildProjectFile(this._doc.name, this._doc.timeline.toJSON());
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${this._doc.name.replace(/[^\w\- ]+/g, '') || 'project'}.novacad`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
    this.setIoStatus(`Saved ${a.download}`);
  }

  /** Open a .novacad file as a NEW project (assets restored as needed). */
  async openProjectFile(file: File): Promise<void> {
    try {
      const { name, timeline } = await restoreProjectFile(await file.text());
      const doc = new CadDocument(
        crypto.randomUUID(),
        name,
        this.kernel,
        Timeline.fromJSON(timeline),
      );
      await doc.save();
      await this.setDocument(doc);
      this.setIoStatus(`Opened ${name}`);
    } catch (err) {
      this.setIoStatus(`Open failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  onUI(fn: () => void): void {
    this.uiListeners.add(fn);
  }

  notifyUI(): void {
    for (const fn of this.uiListeners) fn();
  }

  // --- selection ---------------------------------------------------------------

  select(id: FeatureId | null): void {
    // Completing a pending sweep: the second sketch picked is the path.
    if (
      this.pendingSweepProfile &&
      id &&
      id !== this.pendingSweepProfile &&
      this.doc.timeline.get(id)?.type === 'sketch'
    ) {
      const profile = this.pendingSweepProfile;
      this.pendingSweepProfile = null;
      void this.createSweep(profile, id);
      return;
    }
    this.selection = id ? [id] : [];
    this.syncSelectionUI();
  }

  /** Marquee result: replace the selection (or add to it when shift-dragging). */
  selectByBox(ids: FeatureId[], additive: boolean): void {
    if (additive) {
      for (const id of ids) if (!this.selection.includes(id)) this.selection.push(id);
    } else {
      this.selection = [...ids];
    }
    this.syncSelectionUI();
  }

  /** Shift-click: add/remove a feature from the multi-selection. */
  toggleSelect(id: FeatureId): void {
    const i = this.selection.indexOf(id);
    if (i >= 0) this.selection.splice(i, 1);
    else this.selection.push(id);
    this.syncSelectionUI();
  }

  private syncSelectionUI(): void {
    this.viewport.setSelected(this.selection);
    this.scaleGizmo.sync(this.selected);
    this.moveGizmo.sync(this.selected);
    if (this.postGesture && this.postGesture.featureId !== this.selected) {
      this.postGesture = null;
    }
    this.notifyUI();
  }

  private handleTap(x: number, y: number, additive: boolean): void {
    if (this.edgePick) {
      this.handleEdgePickTap(x, y);
      return;
    }
    if (this.feaPick) {
      this.handleFeaPickTap(x, y);
      return;
    }
    if (this.holePick) {
      this.handleHolePickTap(x, y);
      return;
    }
    if (this.matePick) {
      this.handleMatePickTap(x, y);
      return;
    }
    if (this.measure.active) {
      this.measure.handleTap(x, y);
      return;
    }
    if (this.sketch.active) return;
    const hit = this.viewport.raycastBodies(x, y);
    if (additive && hit) this.toggleSelect(hit.featureId);
    else this.select(hit ? hit.featureId : null);
  }

  // --- tools ---------------------------------------------------------------------

  startSketch(kind: EntityKind = 'rect'): void {
    this.clearStress();
    if (this.measure.active) this.measure.stop();
    // Drop any latched Select sub-mode so it can't reactivate after finishing
    // the sketch (and so Escape acts on the sketch, not the dormant mode).
    this.pushPullMode = false;
    this.orbitMode = false;
    this.select(null);
    this.sketch.start();
    this.sketch.setEntityKind(kind);
  }

  setSketchEntity(kind: EntityKind): void {
    if (!this.sketch.active) this.startSketch(kind);
    else this.sketch.setEntityKind(kind);
  }

  async finishSketch(): Promise<void> {
    const before = new Set(this.doc.timeline.features.map((f) => f.id));
    await this.sketch.finish();
    const added = this.doc.timeline.features.find((f) => !before.has(f.id));
    if (added) this.select(added.id);
  }

  cancelSketch(): void {
    this.sketch.cancel();
  }

  editSketch(id: FeatureId): void {
    this.select(id);
    if (this.sketch.startEditing(id)) this.notifyUI();
  }

  /** Extrude the selected sketch's biggest profile with a starter distance. */
  async extrudeSelected(): Promise<void> {
    const sel = this.selected;
    const feature = sel ? this.doc.timeline.get(sel) : undefined;
    if (!feature || feature.type !== 'sketch') return;
    const n = this.doc.timeline.features.filter((f) => f.type === 'extrude').length + 1;
    let newId: FeatureId | null = null;
    await this.doc.apply(`Extrude ${n}`, (t) => {
      const r = t.addFeature('extrude', `Extrude ${n}`, {
        // No profile path = ALL closed regions (text = one profile per
        // letter; multi-loop sketches extrude together).
        profile: ref(feature.id),
        distance: 10,
        op: 'new-body',
      });
      newId = r.feature.id;
      return r.timeline;
    });
    this.select(newId);
  }

  get canExtrude(): boolean {
    if (!this.selected) return false;
    return this.doc.timeline.get(this.selected)?.type === 'sketch';
  }

  /** Revolve the selected sketch's biggest profile 360° around its Y axis. */
  async revolveSelected(): Promise<void> {
    const sel = this.selected;
    const feature = sel ? this.doc.timeline.get(sel) : undefined;
    if (!feature || feature.type !== 'sketch') return;
    const n = this.doc.timeline.features.filter((f) => f.type === 'revolve').length + 1;
    let newId: FeatureId | null = null;
    await this.doc.apply(`Revolve ${n}`, (t) => {
      const r = t.addFeature('revolve', `Revolve ${n}`, {
        profile: ref(feature.id, 'profile:0'),
        axisOrigin: [0, 0],
        axisDirection: [0, 1],
        angle: Math.PI * 2,
        op: 'new-body',
      });
      newId = r.feature.id;
      return r.timeline;
    });
    this.select(newId);
  }

  /** 2+ sketches selected in click order — loftable stations. */
  get canLoft(): boolean {
    return (
      this.selection.length >= 2 &&
      this.selection.every((id) => this.doc.timeline.get(id)?.type === 'sketch')
    );
  }

  /** Loft the multi-selected sketches IN SELECTION ORDER (hull stations). */
  async loftSelected(): Promise<void> {
    if (!this.canLoft) return;
    const profiles = [...this.selection];
    const n = this.doc.timeline.features.filter((f) => f.type === 'loft').length + 1;
    let newId: FeatureId | null = null;
    await this.doc.apply(`Loft ${n}`, (t) => {
      const r = t.addFeature('loft', `Loft ${n}`, {
        profiles: profiles.map((id) => ref(id, 'profile:0')),
        op: 'new-body',
      });
      newId = r.feature.id;
      return r.timeline;
    });
    this.select(newId);
    const status = newId ? this.doc.lastReport?.statuses.get(newId) : undefined;
    if (status?.state === 'error') {
      this.setIoStatus(`Loft failed: ${status.message}`);
    } else {
      this.setIoStatus('Lofted — set a spine or ruled mode in Properties');
    }
  }

  /** Curvature display: color surfaces by local curvature (fairing check). */
  toggleCurvature(): void {
    this.clearStress(); // display-epoch rebuild drops the colors — drop the legend too
    this.viewport.curvatureOn = !this.viewport.curvatureOn;
    this.refreshBodies();
    this.notifyUI();
  }

  /** True when the selection is a live (unconsumed) body — scalable. */
  get canScale(): boolean {
    const id = this.selected;
    const report = this.doc.lastReport;
    if (!id || !report || report.consumed.has(id)) return false;
    const result = report.results.get(id);
    return result?.kind === 'body' && result.mesh.positions.length > 0;
  }

  /** Add a parametric Scale feature on the selected body (edit factor after). */
  async scaleSelected(): Promise<void> {
    const id = this.selected;
    const result = id ? this.doc.lastReport?.results.get(id) : undefined;
    if (!id || !this.canScale || result?.kind !== 'body') return;
    // Scale origin: the body's bbox center, captured at creation.
    const p = result.mesh.positions;
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    for (let i = 0; i < p.length; i += 3) {
      for (let k = 0; k < 3; k++) {
        const v = p[i + k]!;
        if (v < min[k]!) min[k] = v;
        if (v > max[k]!) max[k] = v;
      }
    }
    const center: [number, number, number] = [
      (min[0]! + max[0]!) / 2,
      (min[1]! + max[1]!) / 2,
      (min[2]! + max[2]!) / 2,
    ];
    const n = this.doc.timeline.features.filter((f) => f.type === 'scale').length + 1;
    let newId: FeatureId | null = null;
    await this.doc.apply(`Scale ${n}`, (t) => {
      const r = t.addFeature('scale', `Scale ${n}`, {
        target: ref(id),
        factors: [1, 1, 1],
        center,
      });
      newId = r.feature.id;
      return r.timeline;
    });
    // A factor-1 scale is invisible — hand the user straight to the controls.
    // Order matters: select() must be the LAST notify so the properties panel
    // render that receives the focus request is not rebuilt again after it.
    this.setIoStatus(
      'Scale added — drag handles: face = one axis, edge = two, corner = uniform · hold ⌥ for about-center',
    );
    this.focusPropertyField = 'Factor X';
    this.select(newId);
  }

  /** Exactly two live bodies selected — matable (first moves onto second). */
  get canMate(): boolean {
    const report = this.doc.lastReport;
    if (this.selection.length !== 2 || !report) return false;
    return this.selection.every((id) => {
      if (report.consumed.has(id)) return false;
      const r = report.results.get(id);
      return r?.kind === 'body' && r.mesh.positions.length > 0;
    });
  }

  /** Mate the FIRST selected body onto the SECOND (bottom → top, stack). */
  async mateSelected(): Promise<void> {
    if (!this.canMate) return;
    const [moving, fixed] = this.selection as [FeatureId, FeatureId];
    const n = this.doc.timeline.features.filter((f) => f.type === 'mate').length + 1;
    let newId: FeatureId | null = null;
    await this.doc.apply(`Mate ${n}`, (t) => {
      const r = t.addFeature('mate', `Mate ${n}`, {
        target: ref(moving),
        to: ref(fixed),
        anchorTarget: 'bottom',
        anchorTo: 'top',
        offset: [0, 0, 0],
      });
      newId = r.feature.id;
      return r.timeline;
    });
    this.setIoStatus(
      'Mated (stacked bottom-on-top) — change anchors/offset in Properties · the mate holds through edits',
    );
    this.select(newId);
  }

  /** Concentric-mate pick: cylindrical face on the mover, then on the base. */
  matePick: { moving: FeatureId; fixed: FeatureId; faceTarget: [number, number, number] | null } | null =
    null;

  startConcentricMate(): void {
    this.clearStress();
    if (!this.canMate) return;
    const [moving, fixed] = this.selection as [FeatureId, FeatureId];
    this.matePick = { moving, fixed, faceTarget: null };
    this.notifyUI();
  }

  cancelMatePick(): void {
    this.matePick = null;
    this.viewport.overlay.clear();
    this.viewport.requestRender();
    this.notifyUI();
  }

  private handleMatePickTap(x: number, y: number): void {
    const mp = this.matePick!;
    const hit = this.viewport.raycastBodies(x, y);
    const expected = mp.faceTarget === null ? mp.moving : mp.fixed;
    if (!hit || hit.featureId !== expected || hit.face?.kind !== 'cylindrical') {
      this.setIoStatus(
        `Click a CYLINDRICAL face on ${this.doc.timeline.get(expected)?.name ?? 'the body'}`,
      );
      return;
    }
    const m = new THREE.Mesh(
      new THREE.SphereGeometry(1.4, 10, 10),
      new THREE.MeshBasicMaterial({ color: 0xb07ce8, depthTest: false }),
    );
    m.position.set(...hit.face.center);
    m.renderOrder = 1200;
    m.raycast = () => undefined;
    this.viewport.overlay.add(m);
    this.viewport.requestRender();
    if (mp.faceTarget === null) {
      mp.faceTarget = hit.face.center;
      this.notifyUI();
      return;
    }
    const faceTarget = mp.faceTarget;
    const faceTo = hit.face.center;
    this.matePick = null;
    this.viewport.overlay.clear();
    void (async () => {
      const n = this.doc.timeline.features.filter((f) => f.type === 'mate').length + 1;
      let newId: FeatureId | null = null;
      await this.doc.apply(`Mate ${n}`, (t) => {
        const r = t.addFeature('mate', `Mate ${n}`, {
          target: ref(mp.moving),
          to: ref(mp.fixed),
          kind: 'concentric',
          anchorTarget: 'center',
          anchorTo: 'center',
          offset: [0, 0, 0],
          faceTarget,
          faceTo,
        });
        newId = r.feature.id;
        return r.timeline;
      });
      this.select(newId);
      const status = newId ? this.doc.lastReport?.statuses.get(newId) : undefined;
      this.setIoStatus(
        status?.state === 'error'
          ? `Concentric mate failed: ${status.message}`
          : 'Axes aligned — position along the axis with Move or an anchor mate',
      );
    })();
  }

  /** True when every multi-selected feature is a live (unconsumed) body. */
  get canGroup(): boolean {
    const report = this.doc.lastReport;
    if (this.selection.length < 2 || !report) return false;
    return this.selection.every((id) => {
      if (report.consumed.has(id)) return false;
      const r = report.results.get(id);
      return r?.kind === 'body' && r.mesh.positions.length > 0;
    });
  }

  /** Group the multi-selected bodies into one (SketchUp-style container). */
  async groupSelected(): Promise<void> {
    if (!this.canGroup) return;
    // Timeline order, so the group lands after all members.
    const ids = [...this.selection].sort(
      (a, b) => this.doc.timeline.positionOf(a) - this.doc.timeline.positionOf(b),
    );
    const n = this.doc.timeline.features.filter((f) => f.type === 'group').length + 1;
    let newId: FeatureId | null = null;
    await this.doc.apply(`Group ${n}`, (t) => {
      const r = t.addFeature('group', `Group ${n}`, { targets: ids.map((id) => ref(id)) });
      newId = r.feature.id;
      return r.timeline;
    });
    this.setIoStatus(`Grouped ${ids.length} bodies — delete the group to ungroup`);
    this.select(newId);
  }

  /** Insert another project as an assembly component (parametric link). */
  async insertPart(projectId: string): Promise<void> {
    if (projectId === this.doc.id) {
      this.setIoStatus('A project cannot contain itself');
      return;
    }
    const rec = await db.projects.get(projectId);
    if (!rec) return;
    const n = this.doc.timeline.features.filter((f) => f.type === 'part').length + 1;
    let newId: FeatureId | null = null;
    await this.doc.apply(`Insert ${rec.name}`, (t) => {
      const r = t.addFeature('part', `${rec.name} ${n}`, {
        projectId,
        source: rec.name,
        position: [0, 0, 0],
        rotationEuler: [0, 0, 0],
      });
      newId = r.feature.id;
      return r.timeline;
    });
    this.activePanel = null;
    this.select(newId);
    const status = newId ? this.doc.lastReport?.statuses.get(newId) : undefined;
    if (status?.state === 'error') {
      this.setIoStatus(`Insert failed: ${status.message}`);
    } else {
      this.setIoStatus(
        `${rec.name} inserted at origin — Move/Rotate to place it · editing that project updates this assembly`,
      );
      this.viewport.fitAll();
    }
  }

  /** Place a component instance of the selected body (source stays live). */
  async instanceSelected(): Promise<void> {
    const id = this.selected;
    const result = id ? this.doc.lastReport?.results.get(id) : undefined;
    if (!id || !this.canScale || result?.kind !== 'body') return;
    const p = result.mesh.positions;
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    for (let i = 0; i < p.length; i += 3) {
      for (let k = 0; k < 3; k++) {
        const v = p[i + k]!;
        if (v < min[k]!) min[k] = v;
        if (v > max[k]!) max[k] = v;
      }
    }
    const n = this.doc.timeline.features.filter((f) => f.type === 'instance').length + 1;
    let newId: FeatureId | null = null;
    await this.doc.apply(`Instance ${n}`, (t) => {
      const r = t.addFeature('instance', `Instance ${n}`, {
        source: ref(id),
        // Offset one bbox-width so the new instance is immediately visible.
        translation: [(max[0]! - min[0]!) * 1.15 + 5, 0, 0],
        rotationAxis: 'Z',
        rotationAngle: 0,
        rotationCenter: [
          (min[0]! + max[0]!) / 2,
          (min[1]! + max[1]!) / 2,
          (min[2]! + max[2]!) / 2,
        ],
        copies: 0,
      });
      newId = r.feature.id;
      return r.timeline;
    });
    this.setIoStatus(
      'Component instance placed — drag it into position (snaps) · editing the source updates all instances',
    );
    this.select(newId);
  }

  /** Add a Move/Rotate feature on the selected body (drag the gizmo after). */
  async moveSelected(): Promise<void> {
    const id = this.selected;
    const result = id ? this.doc.lastReport?.results.get(id) : undefined;
    if (!id || !this.canScale || result?.kind !== 'body') return;
    const p = result.mesh.positions;
    const min = [Infinity, Infinity, Infinity];
    const max = [-Infinity, -Infinity, -Infinity];
    for (let i = 0; i < p.length; i += 3) {
      for (let k = 0; k < 3; k++) {
        const v = p[i + k]!;
        if (v < min[k]!) min[k] = v;
        if (v > max[k]!) max[k] = v;
      }
    }
    const n = this.doc.timeline.features.filter((f) => f.type === 'transform').length + 1;
    let newId: FeatureId | null = null;
    await this.doc.apply(`Move ${n}`, (t) => {
      const r = t.addFeature('transform', `Move ${n}`, {
        target: ref(id),
        translation: [0, 0, 0],
        rotationAxis: 'Z',
        rotationAngle: 0,
        rotationCenter: [
          (min[0]! + max[0]!) / 2,
          (min[1]! + max[1]!) / 2,
          (min[2]! + max[2]!) / 2,
        ],
        copies: 0,
      });
      newId = r.feature.id;
      return r.timeline;
    });
    this.setIoStatus(
      'Move/Rotate added — drag arrows/rings or the body · ⌥ = copy · then type "5x" to array',
    );
    this.select(newId);
  }

  /** Post-gesture typed input: "5x" arrays copies; a number sets exact. */
  private applyPostGesture = (values: number[], raw: string): void => {
    const pg = this.postGesture;
    if (!pg) return;
    const feature = this.doc.timeline.get(pg.featureId);
    if (!feature || (feature.type !== 'transform' && feature.type !== 'instance')) return;
    const params = feature.params as import('@core/timeline/types').TransformParams;

    const arrayMatch = /^\s*(\d+)\s*[xX]\s*$/.exec(raw);
    if (arrayMatch) {
      const copies = Math.max(1, Math.min(200, Number(arrayMatch[1])));
      void this.updateFeatureParams(pg.featureId, { copies });
      this.setIoStatus(`Arrayed ×${copies}`);
      return;
    }
    const value = values[0];
    if (value === undefined || !Number.isFinite(value)) return;
    if (pg.kind === 'move') {
      const len = Math.abs(value);
      void this.updateFeatureParams(pg.featureId, {
        translation: [pg.dir[0] * len, pg.dir[1] * len, pg.dir[2] * len],
      });
      this.setIoStatus(`Moved exactly ${len} mm`);
    } else {
      const sign = Math.sign(params.rotationAngle) || 1;
      void this.updateFeatureParams(pg.featureId, {
        rotationAngle: (sign * Math.abs(value) * Math.PI) / 180,
      });
      this.setIoStatus(`Rotated exactly ${Math.abs(value)}°`);
    }
  };

  /** Selection is a live body with an exact B-rep shape (dress-up-able). */
  get canDressup(): boolean {
    const id = this.selected;
    if (!id || !this.canScale) return false;
    const r = this.doc.lastReport?.results.get(id);
    return r?.kind === 'body' && !!r.brepId;
  }

  /** Hole-pick mode: click face points on one body, Enter drills. */
  holePick: { targetId: FeatureId; picked: { point: [number, number, number]; normal: [number, number, number] }[] } | null =
    null;

  startHolePick(targetId: FeatureId): void {
    this.clearStress();
    this.holePick = { targetId, picked: [] };
    this.select(targetId);
    this.notifyUI();
  }

  cancelHolePick(): void {
    this.holePick = null;
    this.viewport.overlay.clear();
    this.viewport.requestRender();
    this.notifyUI();
  }

  private handleHolePickTap(x: number, y: number): void {
    const hp = this.holePick!;
    const hit = this.viewport.raycastBodies(x, y);
    if (!hit || hit.featureId !== hp.targetId) return;
    hp.picked.push({
      point: [hit.point.x, hit.point.y, hit.point.z],
      normal: [hit.normal.x, hit.normal.y, hit.normal.z],
    });
    const m = new THREE.Mesh(
      new THREE.SphereGeometry(1.4, 10, 10),
      new THREE.MeshBasicMaterial({ color: 0x6db1f0, depthTest: false }),
    );
    m.position.copy(hit.point);
    m.renderOrder = 1200;
    m.raycast = () => undefined;
    this.viewport.overlay.add(m);
    this.viewport.requestRender();
    this.notifyUI();
  }

  async commitHolePick(): Promise<void> {
    const hp = this.holePick;
    if (!hp) return;
    this.holePick = null;
    this.viewport.overlay.clear();
    if (!this.doc.timeline.get(hp.targetId)) return; // target vanished mid-pick
    if (hp.picked.length === 0) {
      this.setIoStatus('No hole positions picked');
      return;
    }
    const n = this.doc.timeline.features.filter((f) => f.type === 'hole').length + 1;
    let newId: FeatureId | null = null;
    await this.doc.apply(`Hole ${n}`, (t) => {
      const r = t.addFeature('hole', `Hole ${n}`, {
        target: ref(hp.targetId),
        holes: hp.picked,
        diameter: 5.5, // M5 clearance
        depth: 'through',
        style: 'plain',
      });
      newId = r.feature.id;
      return r.timeline;
    });
    this.focusPropertyField = 'Diameter';
    this.select(newId);
    const status = newId ? this.doc.lastReport?.statuses.get(newId) : undefined;
    if (status?.state === 'error') this.setIoStatus(`Hole failed: ${status.message}`);
  }

  /**
   * FEA pick mode: click fixture face(s) on one body, Enter, click the load
   * face, type the force (N, pressed into that face) — solve + paint stress.
   */
  feaPick: {
    targetId: FeatureId;
    phase: 'fixtures' | 'load';
    fixtures: [number, number, number][];
  } | null = null;
  /** Stress legend overlay (present while a result is displayed). */
  private feaLegend: HTMLDivElement | null = null;
  feaBusy = false;

  startFeaPick(targetId: FeatureId): void {
    // Exclusive with every other pick/overlay mode (they share the overlay).
    this.cancelFeaPick(); // also removes a lingering load dialog
    if (this.measure.active) this.measure.stop();
    this.edgePick = null;
    this.holePick = null;
    this.matePick = null;
    this.viewport.overlay.clear();
    this.clearStress();
    this.feaPick = { targetId, phase: 'fixtures', fixtures: [] };
    this.select(targetId);
    this.setIoStatus('FEA: click the face(s) to FIX, then press Enter');
    this.notifyUI();
  }

  cancelFeaPick(): void {
    this.feaPick = null;
    this.feaDialog?.remove();
    this.feaDialog = null;
    this.viewport.overlay.clear();
    this.viewport.requestRender();
    this.notifyUI();
  }

  private feaDialog: HTMLDivElement | null = null;

  /** Load dialog: value (N or MPa), direction, mesh resolution. */
  private showFeaLoadDialog(
    clientX: number,
    clientY: number,
    onRun: (v: { magnitude?: number; pressure?: number; resolution: number }) => void,
    onCancel: () => void,
  ): void {
    this.feaDialog?.remove();
    const el = document.createElement('div');
    el.className = 'nv-fea-dialog';
    el.style.cssText =
      'position:fixed;z-index:60;padding:10px 12px;border-radius:8px;background:rgba(24,27,33,.97);' +
      'border:1px solid rgba(255,255,255,.12);color:#cfd6df;font:12px system-ui;box-shadow:0 8px 28px rgba(0,0,0,.5);';
    const row = 'display:flex;gap:6px;align-items:center;margin-bottom:7px';
    const field =
      'background:#14171c;color:#dfe5ec;border:1px solid rgba(255,255,255,.15);border-radius:5px;padding:4px 6px;font:12px system-ui';
    el.innerHTML =
      `<div style="font-weight:600;margin-bottom:7px">Apply load</div>` +
      `<div style="${row}">` +
      `<input data-fea="value" type="number" value="100" step="any" style="${field};width:74px">` +
      `<select data-fea="unit" style="${field}"><option value="N">N (force)</option><option value="MPa">MPa (pressure)</option></select>` +
      `<select data-fea="dir" style="${field}"><option value="into">into face</option><option value="pull">pull away</option></select>` +
      `</div>` +
      `<div style="${row}"><span style="color:#9aa3ae">Mesh</span>` +
      `<select data-fea="res" style="${field}">` +
      `<option value="24">coarse (fast)</option><option value="36" selected>standard</option><option value="60">fine (slow)</option>` +
      `</select></div>` +
      `<div style="display:flex;gap:8px;justify-content:flex-end">` +
      `<button data-fea="cancel" style="${field};cursor:pointer">Cancel</button>` +
      `<button data-fea="run" style="${field};cursor:pointer;background:#2d5f9e;border-color:#3a76c0">Run</button>` +
      `</div>`;
    document.body.appendChild(el);
    const rect = el.getBoundingClientRect();
    el.style.left = `${Math.min(clientX + 10, window.innerWidth - rect.width - 10)}px`;
    el.style.top = `${Math.min(clientY + 10, window.innerHeight - rect.height - 10)}px`;
    this.feaDialog = el;
    const q = <T extends HTMLElement>(k: string): T => el.querySelector(`[data-fea="${k}"]`) as T;
    const input = q<HTMLInputElement>('value');
    input.focus();
    input.select();
    const close = (): void => {
      el.remove();
      if (this.feaDialog === el) this.feaDialog = null;
    };
    const run = (): void => {
      const raw = Number(input.value);
      if (!Number.isFinite(raw) || raw === 0) {
        input.style.borderColor = '#e66';
        return;
      }
      const signed = q<HTMLSelectElement>('dir').value === 'pull' ? -raw : raw;
      const resolution = Number(q<HTMLSelectElement>('res').value);
      close();
      onRun(
        q<HTMLSelectElement>('unit').value === 'MPa'
          ? { pressure: signed, resolution }
          : { magnitude: signed, resolution },
      );
    };
    q<HTMLButtonElement>('run').addEventListener('click', run);
    q<HTMLButtonElement>('cancel').addEventListener('click', () => { close(); onCancel(); });
    el.addEventListener('keydown', (e) => {
      e.stopPropagation(); // the app's global Escape/Enter must not also fire
      if (e.key === 'Enter') run();
      if (e.key === 'Escape') { close(); onCancel(); }
    });
  }

  private feaMarker(point: THREE.Vector3, color: number): void {
    const m = new THREE.Mesh(
      new THREE.SphereGeometry(1.4, 10, 10),
      new THREE.MeshBasicMaterial({ color, depthTest: false }),
    );
    m.position.copy(point);
    m.renderOrder = 1200;
    m.raycast = () => undefined;
    this.viewport.overlay.add(m);
    this.viewport.requestRender();
  }

  private handleFeaPickTap(x: number, y: number): void {
    const fp = this.feaPick!;
    const hit = this.viewport.raycastBodies(x, y);
    if (!hit || hit.featureId !== fp.targetId) return;
    if (fp.phase === 'fixtures') {
      fp.fixtures.push([hit.point.x, hit.point.y, hit.point.z]);
      this.feaMarker(hit.point, 0x59a869); // green = held
      this.setIoStatus(`FEA: ${fp.fixtures.length} fixture face(s) — Enter to continue`);
      return;
    }
    // Load phase: one click picks the face, then the load dialog runs it.
    this.feaMarker(hit.point, 0xe08c4a); // orange = load
    const loadPoint: [number, number, number] = [hit.point.x, hit.point.y, hit.point.z];
    const fixtures = fp.fixtures;
    const targetId = fp.targetId;
    this.showFeaLoadDialog(
      x, y,
      (v) => {
        this.cancelFeaPick();
        void this.runFeaFromUi(targetId, fixtures, loadPoint, v);
      },
      () => {
        this.setIoStatus('FEA cancelled');
        this.cancelFeaPick();
      },
    );
  }

  private feaPickEnter(): void {
    const fp = this.feaPick;
    if (!fp) return;
    if (fp.phase === 'fixtures') {
      if (fp.fixtures.length === 0) {
        this.setIoStatus('FEA: pick at least one fixture face first');
        return;
      }
      fp.phase = 'load';
      this.setIoStatus('FEA: click the face the force pushes on');
      this.notifyUI();
    }
  }

  private async runFeaFromUi(
    targetId: FeatureId,
    fixtures: [number, number, number][],
    loadPoint: [number, number, number],
    load: { magnitude?: number; pressure?: number; resolution: number },
  ): Promise<void> {
    this.feaBusy = true;
    this.setIoStatus('FEA: solving…');
    this.notifyUI();
    try {
      const r = await this.cadApi.runFea({
        bodyId: targetId,
        fixtures: fixtures.map((facePoint) => ({ facePoint })),
        loads: [{ facePoint: loadPoint, magnitude: load.magnitude, pressure: load.pressure }],
        resolution: load.resolution,
      });
      this.setIoStatus(
        r.converged
          ? `FEA: max ${r.maxVonMisesMPa} MPa · FoS ${r.factorOfSafety} · max deflection ${r.maxDisplacementMm} mm`
          : `FEA did not converge — ${r.note ?? 'check fixtures'}`,
      );
    } catch (err) {
      this.setIoStatus(`FEA failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      this.feaBusy = false;
      this.notifyUI();
    }
  }

  /** CadApi hook: paint the ramp + deformed shape + show the legend. */
  showStressResult(
    id: FeatureId,
    vertexVonMises: Float32Array,
    info: {
      maxVonMises: number;
      factorOfSafety: number;
      material: string;
      displacement: Float32Array;
      maxDisplacement: number;
      deformationScale: number;
    },
  ): void {
    // Auto exaggeration: deform to ~8% of the body's diagonal, whole factors.
    // An explicit scale must be finite — an LLM Infinity/NaN would write
    // non-finite coordinates into the deformed position buffer.
    let scale = Number.isFinite(info.deformationScale) ? info.deformationScale : 0;
    if (scale > 0) {
      scale = Math.min(500, scale); // cap runaway explicit exaggeration
    } else {
      const bbox = this.cadApi.bodyInfo(id)?.bbox;
      const diag = bbox
        ? Math.hypot(
            bbox.max[0] - bbox.min[0], bbox.max[1] - bbox.min[1], bbox.max[2] - bbox.min[2],
          )
        : 0;
      scale =
        info.maxDisplacement > 1e-9 && diag > 0
          ? Math.max(1, Math.min(500, Math.round((0.08 * diag) / info.maxDisplacement)))
          : 0;
    }
    if (!this.viewport.showStress(id, vertexVonMises, info.maxVonMises, info.displacement, scale)) return;
    this.feaLegend?.remove();
    const el = document.createElement('div');
    el.className = 'nv-fea-legend';
    el.style.cssText =
      'position:absolute;left:12px;bottom:44px;z-index:30;padding:8px 12px;border-radius:8px;' +
      'background:rgba(20,23,28,.92);color:#cfd6df;font:11px system-ui;pointer-events:none;';
    const fos = info.factorOfSafety;
    el.innerHTML =
      `<div style="font-weight:600;margin-bottom:4px">von Mises · ${info.material}</div>` +
      `<div style="width:160px;height:10px;border-radius:3px;background:linear-gradient(90deg,#1a1ae6,#1ad91a,#e61a1a)"></div>` +
      `<div style="display:flex;justify-content:space-between;margin-top:2px"><span>0</span><span>${info.maxVonMises.toFixed(1)} MPa</span></div>` +
      `<div style="margin-top:4px">Factor of safety: <b style="color:${fos < 1 ? '#e66' : fos < 2 ? '#ec5' : '#7d7'}">${fos.toFixed(2)}</b></div>` +
      (scale > 0
        ? `<div style="margin-top:2px;color:#9aa3ae">deformation ×${scale} exaggerated · Esc clears</div>`
        : `<div style="margin-top:2px;color:#9aa3ae">Esc clears</div>`);
    this.viewport.element.appendChild(el);
    this.feaLegend = el;
  }

  /** Remove the stress overlay + legend (Escape, edits, project switch). */
  clearStress(): void {
    if (!this.feaLegend) return;
    this.feaLegend.remove();
    this.feaLegend = null;
    this.viewport.clearStressView();
  }

  /** Pattern the selected body (linear ×3 starter; edit in Properties). */
  async patternSelected(): Promise<void> {
    const id = this.selected;
    const result = id ? this.doc.lastReport?.results.get(id) : undefined;
    if (!id || !this.canScale || result?.kind !== 'body') return;
    const p = result.mesh.positions;
    let minX = Infinity, maxX = -Infinity;
    for (let i = 0; i < p.length; i += 3) {
      minX = Math.min(minX, p[i]!);
      maxX = Math.max(maxX, p[i]!);
    }
    const n = this.doc.timeline.features.filter((f) => f.type === 'pattern').length + 1;
    let newId: FeatureId | null = null;
    await this.doc.apply(`Pattern ${n}`, (t) => {
      const r = t.addFeature('pattern', `Pattern ${n}`, {
        target: ref(id),
        kind: 'linear',
        count: 3,
        step: [Math.max(10, (maxX - minX) * 1.2), 0, 0],
      });
      newId = r.feature.id;
      return r.timeline;
    });
    this.setIoStatus('Pattern ×3 added — count takes expressions (e.g. a parameter)');
    this.focusPropertyField = 'Count';
    this.select(newId);
  }

  /** Drop a parametric helical spring/thread coil at the origin. */
  async addCoil(): Promise<void> {
    const n = this.doc.timeline.features.filter((f) => f.type === 'coil').length + 1;
    let newId: FeatureId | null = null;
    await this.doc.apply(`Coil ${n}`, (t) => {
      const r = t.addFeature('coil', `Coil ${n}`, {
        axisOrigin: [0, 0, 0],
        axis: 'Z',
        radius: 15,
        pitch: 8,
        turns: 5,
        wireDiameter: 3,
        op: 'new-body',
      });
      newId = r.feature.id;
      return r.timeline;
    });
    const status = newId ? this.doc.lastReport?.statuses.get(newId) : undefined;
    if (status?.state === 'error') {
      this.setIoStatus(`Coil failed: ${status.message}`);
      return;
    }
    this.setIoStatus('Coil added — radius/pitch/turns take expressions in Properties');
    this.focusPropertyField = 'Radius';
    this.select(newId);
  }

  /** Add a mold draft on a body's walls (2° about z=0 by default). */
  async addDraft(targetId: FeatureId): Promise<void> {
    const n = this.doc.timeline.features.filter((f) => f.type === 'draft').length + 1;
    let newId: FeatureId | null = null;
    await this.doc.apply(`Draft ${n}`, (t) => {
      const r = t.addFeature('draft', `Draft ${n}`, {
        target: ref(targetId),
        angleDeg: 2,
        axis: 'Z',
        neutralOffset: 0,
      });
      newId = r.feature.id;
      return r.timeline;
    });
    const status = newId ? this.doc.lastReport?.statuses.get(newId) : undefined;
    if (status?.state === 'error') {
      this.setIoStatus(`Draft failed: ${status.message} — adjust in Properties`);
    }
    this.focusPropertyField = 'Angle °';
    this.select(newId);
  }

  /** Exact mass properties of the selected body (B-rep kernel). */
  async massPropsSelected(): Promise<void> {
    const id = this.selected;
    const result = id ? this.doc.lastReport?.results.get(id) : undefined;
    if (!id || result?.kind !== 'body' || !result.brepId || !this.kernel.massProps) {
      this.setIoStatus('Mass properties need an exact B-rep body');
      return;
    }
    try {
      const mp = await this.kernel.massProps(result.brepId);
      const name = this.doc.timeline.get(id)?.name ?? 'Body';
      const cm3 = mp.volume / 1000;
      const c = mp.centerOfMass.map((v) => v.toFixed(2)).join(', ');
      // Common densities in g/cm³ — enough for a sanity check without a UI.
      const mass = (d: number): string => `${(cm3 * d).toFixed(1)}g`;
      this.setIoStatus(
        `${name}: V=${cm3.toFixed(2)}cm³ · A=${(mp.area / 100).toFixed(1)}cm² · ` +
          `CoM=(${c})mm · mass: PLA ${mass(1.24)} · alu ${mass(2.7)} · steel ${mass(7.85)}`,
      );
    } catch (err) {
      this.setIoStatus(`Mass properties failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  /** Exact interference (overlap volume) between the two selected bodies. */
  async checkInterference(): Promise<void> {
    const [a, b] = this.selection;
    const ra = a ? this.doc.lastReport?.results.get(a) : undefined;
    const rb = b ? this.doc.lastReport?.results.get(b) : undefined;
    if (
      this.selection.length !== 2 ||
      ra?.kind !== 'body' || !ra.brepId ||
      rb?.kind !== 'body' || !rb.brepId ||
      !this.kernel.interference
    ) {
      this.setIoStatus('Interference check needs two B-rep bodies selected (⇧click)');
      return;
    }
    try {
      const v = await this.kernel.interference(ra.brepId, rb.brepId);
      const na = this.doc.timeline.get(a!)?.name;
      const nb = this.doc.timeline.get(b!)?.name;
      this.setIoStatus(
        v > 1e-6
          ? `⚠ ${na} and ${nb} INTERFERE — overlap ${(v / 1000).toFixed(3)}cm³ (${v.toFixed(1)}mm³)`
          : `✓ No interference between ${na} and ${nb}`,
      );
    } catch (err) {
      this.setIoStatus(`Interference check failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  /** Edge-pick mode: click edges of one body, Enter applies fillet/chamfer. */
  edgePick: { targetId: FeatureId; kind: 'fillet' | 'chamfer'; picked: [number, number, number][] } | null =
    null;

  startEdgePick(kind: 'fillet' | 'chamfer', targetId: FeatureId): void {
    this.clearStress();
    this.edgePick = { targetId, kind, picked: [] };
    this.select(targetId);
    this.notifyUI();
  }

  cancelEdgePick(): void {
    this.edgePick = null;
    this.viewport.overlay.clear();
    this.viewport.requestRender();
    this.notifyUI();
  }

  /** Tap during edge picking: toggle the edge under the cursor. */
  private handleEdgePickTap(x: number, y: number): void {
    const ep = this.edgePick!;
    const hit = this.viewport.raycastEdge(x, y, ep.targetId);
    if (!hit) return;
    const idx = ep.picked.findIndex(
      (p) => Math.hypot(p[0] - hit.midpoint[0], p[1] - hit.midpoint[1], p[2] - hit.midpoint[2]) < 1e-6,
    );
    if (idx >= 0) ep.picked.splice(idx, 1);
    else ep.picked.push(hit.midpoint);
    // Markers on picked edge midpoints.
    this.viewport.overlay.clear();
    for (const p of ep.picked) {
      const m = new THREE.Mesh(
        new THREE.SphereGeometry(1.2, 10, 10),
        new THREE.MeshBasicMaterial({ color: 0xe8c66a, depthTest: false }),
      );
      m.position.set(p[0], p[1], p[2]);
      m.renderOrder = 1200;
      m.raycast = () => undefined;
      this.viewport.overlay.add(m);
    }
    this.viewport.requestRender();
    this.notifyUI();
  }

  /** Enter during edge picking: apply the dress-up to the picked edges. */
  async commitEdgePick(): Promise<void> {
    const ep = this.edgePick;
    if (!ep) return;
    this.edgePick = null;
    this.viewport.overlay.clear();
    if (!this.doc.timeline.get(ep.targetId)) return; // target vanished mid-pick
    try {
      await this.addDressup(ep.kind, ep.targetId, ep.picked.length > 0 ? ep.picked : undefined);
    } catch (err) {
      this.setIoStatus(`${ep.kind} failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  /** Fillet / chamfer / shell — B-rep dress-up features on a body. */
  async addDressup(
    kind: 'fillet' | 'chamfer' | 'shell',
    targetId: FeatureId,
    edges?: readonly [number, number, number][],
  ): Promise<void> {
    const n = this.doc.timeline.features.filter((f) => f.type === kind).length + 1;
    const label = kind[0]!.toUpperCase() + kind.slice(1);
    let newId: FeatureId | null = null;
    await this.doc.apply(`${label} ${n}`, (t) => {
      const params =
        kind === 'fillet'
          ? { target: ref(targetId), radius: 2, ...(edges ? { edges } : {}) }
          : kind === 'chamfer'
            ? { target: ref(targetId), distance: 1, ...(edges ? { edges } : {}) }
            : { target: ref(targetId), thickness: 2, open: 'top' as const };
      const r = t.addFeature(kind, `${label} ${n}`, params as never);
      newId = r.feature.id;
      return r.timeline;
    });
    this.focusPropertyField =
      kind === 'fillet' ? 'Radius' : kind === 'chamfer' ? 'Distance' : 'Thickness';
    this.select(newId);
    const status = newId ? this.doc.lastReport?.statuses.get(newId) : undefined;
    if (status?.state === 'error') {
      this.setIoStatus(`${label} failed: ${status.message} — adjust in Properties`);
    }
  }

  /** Export the exact B-rep solids of all visible bodies as STEP. */
  async exportStepFile(): Promise<void> {
    const report = this.doc.lastReport;
    const ids: string[] = [];
    const names: string[] = [];
    let meshOnly = 0;
    if (report) {
      for (const [id, r] of report.results) {
        if (r.kind !== 'body' || report.consumed.has(id) || r.mesh.positions.length === 0) continue;
        if (r.brepId) {
          ids.push(r.brepId);
          names.push(this.doc.timeline.get(id)?.name ?? id);
        } else meshOnly++;
      }
    }
    if (ids.length === 0) {
      this.setIoStatus('No B-rep bodies to export — is the B-rep kernel active?');
      return;
    }
    try {
      // Named XCAF products: each body arrives in downstream CAD with its
      // feature name instead of an anonymous compound.
      const text = await this.kernel.exportStep!(ids, names);
      const blob = new Blob([text], { type: 'model/step' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${this.doc.name.replace(/[^\w\- ]+/g, '') || 'model'}.step`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
      this.setIoStatus(
        `STEP exported (${ids.length} solid${ids.length > 1 ? 's' : ''}` +
          (meshOnly ? `, ${meshOnly} mesh-only body skipped)` : ')'),
      );
    } catch (err) {
      this.setIoStatus(`STEP export failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  /** Mirror the selected body across the world YZ plane (edit in properties). */
  async mirrorSelected(): Promise<void> {
    const id = this.selected;
    if (!id || !this.canScale) return;
    const n = this.doc.timeline.features.filter((f) => f.type === 'mirror').length + 1;
    let newId: FeatureId | null = null;
    await this.doc.apply(`Mirror ${n}`, (t) => {
      const r = t.addFeature('mirror', `Mirror ${n}`, {
        target: ref(id),
        plane: 'YZ',
        offset: 0,
        keepOriginal: true,
      });
      newId = r.feature.id;
      return r.timeline;
    });
    this.setIoStatus('Mirrored across YZ@0 — change plane/offset in Properties');
    this.focusPropertyField = 'Offset';
    this.select(newId);
  }

  /** Arm the sweep flow: selected sketch = profile; next sketch picked = path. */
  startSweep(): void {
    if (!this.canExtrude || !this.selected) return;
    this.pendingSweepProfile = this.selected;
    this.notifyUI();
  }

  private async createSweep(profileId: FeatureId, pathId: FeatureId): Promise<void> {
    const n = this.doc.timeline.features.filter((f) => f.type === 'sweep').length + 1;
    let newId: FeatureId | null = null;
    await this.doc.apply(`Sweep ${n}`, (t) => {
      const r = t.addFeature('sweep', `Sweep ${n}`, {
        profile: ref(profileId, 'profile:0'),
        path: ref(pathId),
        op: 'new-body',
      });
      newId = r.feature.id;
      return r.timeline;
    });
    this.selection = newId ? [newId] : [];
    this.syncSelectionUI();
  }

  /** Merge a partial params patch into any feature (extrude/revolve edits). */
  async updateFeatureParams(id: FeatureId, patch: Record<string, unknown>): Promise<void> {
    const feature = this.doc.timeline.get(id);
    if (!feature) return;
    const params = { ...(feature.params as unknown as Record<string, unknown>), ...patch };
    // Patching a key to undefined removes it (e.g. clearing constraints).
    for (const k of Object.keys(params)) {
      if (params[k] === undefined) delete params[k];
    }
    try {
      await this.doc.apply(`Edit ${feature.name}`, (t) =>
        t.updateFeature(id, params as never),
      );
    } catch (err) {
      // Structural refusals (cycles, missing deps) surface as status, not
      // as unhandled rejections from void-called UI handlers.
      this.setIoStatus(`Edit rejected: ${err instanceof Error ? err.message : err}`);
    }
  }

  // --- import / export (worker-backed) ---------------------------------------------

  togglePanel(panel: 'ai' | 'assets' | 'bodies' | 'projects' | 'assistant' | 'params'): void {
    this.activePanel = this.activePanel === panel ? null : panel;
    this.notifyUI();
  }

  setIoStatus(msg: string): void {
    this.ioStatus = msg;
    this.notifyUI();
    if (msg) setTimeout(() => this.setIoStatus(''), 4000);
  }

  /** Import STL/OBJ files: parse in the worker, store, add timeline features. */
  async importMeshFiles(files: readonly File[]): Promise<void> {
    for (const file of files) {
      try {
        const buffer = await file.arrayBuffer();
        const hash = hashBytes(new Uint8Array(buffer));
        const ext = file.name.toLowerCase().split('.').pop() ?? '';
        const brepFormat =
          ext === 'step' || ext === 'stp' ? 'step' : ext === 'iges' || ext === 'igs' ? 'iges' : null;
        // STEP/IGES → EXACT import when the B-rep kernel is up: the part gets
        // a real shape (fillets/booleans/mass props), not a display mesh.
        // Wait for the WASM download to settle: routing on mode==='pending'
        // could commit an import-step feature that then can never evaluate.
        if (
          brepFormat &&
          this.kernel instanceof BrepKernel &&
          (await this.kernel.whenReady()) === 'brep'
        ) {
          // 'brep:' namespace keeps the raw-text record from colliding with a
          // packed-mesh record of the SAME file bytes (mesh-mode import).
          await this.addImportedBrep(
            file.name.replace(/\.[^.]+$/, ''),
            `brep:${hash}`,
            new TextDecoder().decode(buffer),
            brepFormat,
          );
          this.setIoStatus(`Imported ${file.name} as an exact B-rep solid`);
          continue;
        }
        const mesh = await parseMeshFile(file.name, buffer);
        await this.addImportedMesh(file.name.replace(/\.[^.]+$/, ''), hash, mesh);
        this.setIoStatus(`Imported ${file.name}`);
      } catch (err) {
        this.setIoStatus(`Import failed: ${err instanceof Error ? err.message : err}`);
      }
    }
  }

  /** Store the STEP/IGES text + add an exact import-step feature. */
  async addImportedBrep(
    name: string,
    hash: string,
    text: string,
    format: 'step' | 'iges',
  ): Promise<void> {
    await saveBrepText(name, hash, text);
    let newId: FeatureId | null = null;
    await this.doc.apply(`Import ${name}`, (t) => {
      const r = t.addFeature('import-step', name, { assetHash: hash, name, format });
      newId = r.feature.id;
      return r.timeline;
    });
    const status = newId ? this.doc.lastReport?.statuses.get(newId) : undefined;
    if (status?.state === 'error') {
      this.setIoStatus(`Import failed: ${status.message}`);
      return;
    }
    this.select(newId);
    this.viewport.fitAll();
  }

  /** Shared with the asset browser: store parsed mesh + add import-mesh feature. */
  async addImportedMesh(name: string, hash: string, mesh: MeshData): Promise<void> {
    await saveParsedMesh(name, hash, mesh);
    let newId: FeatureId | null = null;
    await this.doc.apply(`Import ${name}`, (t) => {
      const r = t.addFeature('import-mesh', name, {
        assetHash: hash,
        name,
        transform: { position: [0, 0, 0], rotationEuler: [0, 0, 0], scale: [1, 1, 1] },
      });
      newId = r.feature.id;
      return r.timeline;
    });
    this.select(newId);
    this.viewport.fitAll();
  }

  /** Export all evaluated bodies via the io worker, then download. */
  async exportAs(format: ExportFormat): Promise<void> {
    const report = this.doc.lastReport;
    const bodies: MeshData[] = [];
    if (report) {
      for (const r of report.results.values()) {
        if (r.kind === 'body' && r.mesh.positions.length > 0) bodies.push(r.mesh);
      }
    }
    if (bodies.length === 0) {
      this.setIoStatus('Nothing to export — the model has no bodies');
      return;
    }
    try {
      const blob = await exportMeshes(format, bodies);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${this.doc.name || 'novacad'}.${format}`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
      this.setIoStatus(`Exported ${bodies.length} bod${bodies.length > 1 ? 'ies' : 'y'} → ${a.download}`);
    } catch (err) {
      this.setIoStatus(`Export failed: ${err instanceof Error ? err.message : err}`);
    }
  }

  // --- feature operations -----------------------------------------------------------

  async renameSelected(name: string): Promise<void> {
    if (!this.selected || !name.trim()) return;
    await this.doc.apply(`Rename to ${name}`, (t) => t.renameFeature(this.selected!, name.trim()));
  }

  async setSuppressed(id: FeatureId, suppressed: boolean): Promise<void> {
    const name = this.doc.timeline.get(id)?.name ?? 'feature';
    await this.doc.apply(`${suppressed ? 'Suppress' : 'Unsuppress'} ${name}`, (t) =>
      t.setSuppressed(id, suppressed),
    );
  }

  async deleteFeature(id: FeatureId): Promise<void> {
    const t = this.doc.timeline;
    const feature = t.get(id);
    if (!feature) return;
    const dependents = t.downstreamOf(id).filter((d) => d !== id);
    if (dependents.length > 0) {
      const names = dependents.map((d) => t.get(d)?.name ?? d).join(', ');
      if (!confirm(`Deleting ${feature.name} also deletes: ${names}. Continue?`)) return;
    }
    if (this.selected === id || dependents.includes(this.selected!)) this.select(null);
    await this.doc.apply(`Delete ${feature.name}`, (tl) =>
      tl.removeFeature(id, { cascade: true }),
    );
  }

  async setRollback(index: number): Promise<void> {
    await this.doc.apply('Move rollback bar', (t) => t.setRollback(index));
  }

  // --- keyboard -----------------------------------------------------------------------

  private onKeyDown = (e: KeyboardEvent): void => {
    // Never let hotkeys (mode toggles, Backspace=delete) fire while the user
    // is typing in ANY editable field — inputs, selects, textareas (the AI
    // prompt), or contentEditable hosts.
    if (
      e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLSelectElement ||
      e.target instanceof HTMLTextAreaElement ||
      (e.target instanceof HTMLElement && e.target.isContentEditable)
    ) {
      return;
    }
    const mod = e.metaKey || e.ctrlKey;

    if (mod && (e.key === 'z' || e.key === 'Z')) {
      e.preventDefault();
      // Inside a sketch, ⌘Z steps through DRAWING actions — never document
      // undo, which would rip features out from under the open sketch.
      if (this.sketch.active) {
        if (e.shiftKey) this.sketch.redo();
        else this.sketch.undo();
        return;
      }
      void (e.shiftKey ? this.doc.redo() : this.doc.undo());
      return;
    }
    if (mod && e.key === 'y') {
      e.preventDefault();
      void this.doc.redo();
      return;
    }
    if (mod) return;

    if (e.key === '?') {
      this.cheatsheetOpen = !this.cheatsheetOpen;
      this.notifyUI();
      return;
    }
    if (e.key === 'Escape') {
      if (this.cheatsheetOpen) {
        this.cheatsheetOpen = false;
        this.notifyUI();
        return;
      }
      if (this.drawingOpen) {
        this.drawingOpen = false;
        this.notifyUI();
        return;
      }
      if (this.postGesture) {
        this.postGesture = null;
        this.dim.hide();
        return;
      }
      if (this.edgePick) {
        this.cancelEdgePick();
        return;
      }
      if (this.feaPick) {
        this.cancelFeaPick();
        return;
      }
      if (this.holePick) {
        this.cancelHolePick();
        return;
      }
      if (this.matePick) {
        this.cancelMatePick();
        return;
      }
      if (this.measure.active) {
        this.measure.stop();
        return;
      }
      if (this.pendingSweepProfile) {
        this.pendingSweepProfile = null;
        this.notifyUI();
        return;
      }
      if (this.pushPullMode || this.orbitMode) {
        this.pushPullMode = false;
        this.orbitMode = false;
        this.notifyUI();
        return;
      }
      // After all interaction modes: a displayed stress result.
      if (this.feaLegend) {
        this.clearStress();
        return;
      }
      const r = this.sketch.handleEscape();
      if (r === 'inactive') this.select(null);
      return;
    }
    // Post-gesture window: digits open the dimension box ("5x", "40"…).
    if (this.postGesture && !this.sketch.active && /^[\d.]$/.test(e.key)) {
      const pg = this.postGesture;
      this.dim.show('distance or Nx', pg.clientX, pg.clientY, this.applyPostGesture);
      this.dim.beginEdit(e.key);
      e.preventDefault();
      return;
    }
    if (e.key === 'Enter') {
      if (this.edgePick) {
        e.preventDefault();
        void this.commitEdgePick();
        return;
      }
      if (this.feaPick) {
        e.preventDefault();
        this.feaPickEnter();
        return;
      }
      if (this.holePick) {
        e.preventDefault();
        void this.commitHolePick();
        return;
      }
      if (this.sketch.handleEnter()) e.preventDefault();
      return;
    }
    if ((e.key === 'Delete' || e.key === 'Backspace') && this.selected && !this.sketch.active) {
      e.preventDefault();
      void this.deleteFeature(this.selected);
      return;
    }
    // Tool shortcuts + dimension typing.
    if (this.sketch.active && /^[\d.\-]$/.test(e.key)) {
      if (this.sketch.handleTypedChar(e.key)) e.preventDefault();
      return;
    }
    if (e.key.toLowerCase() === 'm' && !this.sketch.active) {
      this.toggleMeasure();
      return;
    }
    if (e.key.toLowerCase() === 'p' && !this.sketch.active) {
      this.togglePushPull();
      return;
    }
    if (e.key.toLowerCase() === 'o' && !this.sketch.active) {
      this.toggleOrbit();
      return;
    }
    const toolKeys: Record<string, EntityKind> = {
      l: 'line',
      r: 'rect',
      c: 'circle',
      e: 'ellipse',
      a: 'arc2', // SketchUp's A = 2-point arc
      s: 'spline',
      t: 'text',
      p: 'polygon',
      o: 'slot',
    };
    if (e.key.toLowerCase() === 'x' && this.sketch.active) {
      this.sketch.toggleTrim();
      return;
    }
    const kind = toolKeys[e.key.toLowerCase()];
    if (kind && this.sketch.active) this.sketch.setEntityKind(kind);
  };
}
